import { faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardProps,
  Stack,
  Switch,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddSinglePanelToSwitchgearDialog } from "../hvSubstations/AddSinglePanelToSwitchgearDialog";
import { UpdatePanelNameDialog } from "../hvSubstations/UpdatePanelNameDialog";
import { Snackbar } from "../shared/Snackbar";
import { managedSurveyAssetType, ManagedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { getSubAssetType } from "../shared/interfaces/SurveyableAsset";
import { Panel } from "../shared/interfaces/Switchgear";
import { PartialResultReducer, PartialResultSwitchgear } from "./PartialResultReducer";

interface Props {
  resultPanelNo: string;
  resultPanelName: string;
  panelAssets?: Panel[];
  partialResultReducer?: PartialResultReducer;
  cardProps?: CardProps;
  onPanelUpdate: () => void;
  sx?: SxProps<Theme>;
  parentAssetId?: number;
  parentAssetType?: ManagedSurveyAssetType;
}

export function PartialResultPanelAssetUI(props: Props) {
  const theme = useTheme();
  const { t } = useTranslation();

  const { onPanelUpdate } = props;

  const panelAssetMatchFound = props.partialResultReducer?.checkPanelMatch(
    props.resultPanelNo,
    props.parentAssetId,
    props.panelAssets,
  );
  const partialResultPanel = props.partialResultReducer?.getPartialResultPanel(
    props.resultPanelNo,
    props.parentAssetId,
  );
  const [createPanelDialogOpen, setCreatePanelDialogOpen] = useState(false);
  const [updatePanelNameDialogOpen, setUpdatePanelNameDialogOpen] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const panelAsset = useMemo(() => {
    return props.panelAssets?.find((panel) => panel.panelNo === props.resultPanelNo);
  }, [props.panelAssets, props.resultPanelNo]);

  const iconColor = useMemo(() => {
    if (partialResultPanel?.isExcluded) return theme.palette.text.disabled;
    else if (panelAssetMatchFound) return theme.palette.success.main;
    else return theme.palette.warning.main;
  }, [partialResultPanel?.isExcluded, panelAssetMatchFound, theme]);

  const handleCreatePanelButtonClick = useCallback(() => {
    setCreatePanelDialogOpen(true);
  }, []);

  const handleUpdatePanelNameButtonClick = useCallback(() => {
    setUpdatePanelNameDialogOpen(true);
  }, []);

  const handleSavePanelSuccess = useCallback(
    (panelNumber: string) => {
      onPanelUpdate();
      setSnackbarText(t("panels.panelAdded", { panelNo: panelNumber, val: getSubAssetType(props.parentAssetType) }));
      setShowSnackbar(true);
      setCreatePanelDialogOpen(false);
    },
    [onPanelUpdate, t, props.parentAssetType],
  );

  const handlePanelNameUpdateSuccess = useCallback(
    (panelNumber: string) => {
      onPanelUpdate();
      setSnackbarText(t("panels.panelUpdated", { panelNo: panelNumber, val: getSubAssetType(props.parentAssetType) }));
      setShowSnackbar(true);
      setUpdatePanelNameDialogOpen(false);
    },
    [onPanelUpdate, t, props.parentAssetType],
  );

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const handleExcludePanelSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      if (!props.parentAssetId) {
        props.partialResultReducer?.setPanelExcludedStatus(props.resultPanelNo, !checked);
      } else {
        props.partialResultReducer?.setGroupPanelExcludedStatus(props.resultPanelNo, !checked, props.parentAssetId);
      }
    },
    [props.parentAssetId, props.partialResultReducer, props.resultPanelNo],
  );

  const switchgearId =
    props.partialResultReducer?.state.selectedSwitchgear?.id ??
    props.partialResultReducer?.state.selectedSwitchgears?.find(
      (selectedSwitchgear: PartialResultSwitchgear) => selectedSwitchgear.assetId === props.parentAssetId,
    )?.assetId;

  const subAsssetlabel = getSubAssetType(props.parentAssetType);

  return (
    <>
      <Box display="flex" justifyContent="center" sx={props.sx}>
        <Card {...props.cardProps}>
          <Stack height="100%" spacing={1}>
            <Box display="flex" justifyContent="flex-end">
              <Tooltip
                title={
                  partialResultPanel?.isExcluded
                    ? t("panels.includePanel", { val: subAsssetlabel })
                    : t("panels.excludePanel", { val: subAsssetlabel })
                }
              >
                <Switch
                  checked={!partialResultPanel?.isExcluded}
                  size="small"
                  onChange={handleExcludePanelSwitchChange}
                />
              </Tooltip>
            </Box>
            <Stack display="flex" flex={1} justifyContent="space-between" alignItems="center" p={0.5} spacing={2}>
              <Tooltip
                title={
                  partialResultPanel?.isExcluded
                    ? t("panels.panelExcludedDescription", { val: subAsssetlabel })
                    : undefined
                }
              >
                <CardContent sx={{ padding: 0 }}>
                  <Stack spacing={1.5} justifyContent="center" alignItems="center">
                    {partialResultPanel?.isExcluded ? (
                      <UnpublishedIcon color="disabled" sx={{ fontSize: "3em" }} />
                    ) : (
                      <FontAwesomeIcon
                        icon={panelAssetMatchFound ? faCheckCircle : faExclamationTriangle}
                        size="3x"
                        color={iconColor}
                      />
                    )}
                    <Typography color={"textPrimary"} gutterBottom variant="subtitle2" component="div" align="center">
                      {partialResultPanel?.isExcluded
                        ? t("panels.panelExcluded", { val: subAsssetlabel })
                        : panelAssetMatchFound
                          ? t("panels.matchFound")
                          : t("panels.panelNotFound", {
                              panelNo: props.resultPanelNo,
                              entity: subAsssetlabel,
                            })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={partialResultPanel?.isExcluded ? theme.palette.text.disabled : "textSecondary"}
                      align="center"
                    >
                      {<Stack alignItems="center">{props.resultPanelName}</Stack>}
                    </Typography>
                  </Stack>
                </CardContent>
              </Tooltip>
              <CardActions sx={{ padding: 0 }}>
                <Box display="flex" flex={1} justifyContent="center">
                  {!partialResultPanel?.isExcluded && (
                    <>
                      {!panelAssetMatchFound && (
                        <Button
                          size="small"
                          color="primary"
                          sx={{
                            textTransform: "uppercase",
                            fontSize: "0.75em",
                          }}
                          onClick={handleCreatePanelButtonClick}
                        >
                          {t("panels.createPanel", { val: subAsssetlabel })}
                        </Button>
                      )}
                      {panelAssetMatchFound &&
                        panelAsset?.name !== props.resultPanelName &&
                        props.parentAssetType !== managedSurveyAssetType.Transformer && (
                          <Button
                            size="small"
                            color="primary"
                            sx={{
                              textTransform: "uppercase",
                              fontSize: "0.75em",
                            }}
                            onClick={handleUpdatePanelNameButtonClick}
                          >
                            {t("panels.updatePanelName", { val: subAsssetlabel })}
                          </Button>
                        )}
                    </>
                  )}
                </Box>
              </CardActions>
            </Stack>
          </Stack>
        </Card>
      </Box>
      {switchgearId && (
        <AddSinglePanelToSwitchgearDialog
          switchgearId={switchgearId}
          visible={createPanelDialogOpen}
          onSuccess={handleSavePanelSuccess}
          onClose={() => setCreatePanelDialogOpen(false)}
          prepopulatedPanelDetails={{
            panelNo: props.resultPanelNo,
            name: props.resultPanelName,
          }}
          existingPanels={props.panelAssets}
          enableEditingPanelNo={!props.resultPanelNo || props.resultPanelNo === ""}
          parentAssetType={props.parentAssetType}
        />
      )}

      {switchgearId && (
        <UpdatePanelNameDialog
          visible={updatePanelNameDialogOpen}
          onSuccess={handlePanelNameUpdateSuccess}
          onClose={() => setUpdatePanelNameDialogOpen(false)}
          existingPanelDetails={{
            panelNo: props.resultPanelNo,
            name: props.resultPanelName,
            panelAssetId: panelAsset?.panelAssetId,
            assetType: props.parentAssetType,
          }}
        />
      )}

      <Snackbar open={showSnackbar} onClose={handleSnackbarClose} message={snackbarText} />
    </>
  );
}
